import type {ReactNode} from 'react'
import {EyeIcon, CommentDiscussionIcon, PeopleIcon} from '@primer/octicons-react'
import type {
  ReviewRequestsData,
  CompletedReview,
  PendingReviewRequest,
} from '../../../page-data/payloads/review-requests'
import {ActionItem} from './ActionItem'
import styles from './ActionItems.module.css'
import {pullRequestsChangesRoute, pullRequestsChangesWithRangeRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {pullRequestsOverviewRoute} from '../../../routes/overview-route'

interface ReviewerActionsProps {
  reviewers: ReviewRequestsData
  viewerLogin: string
}

export function ReviewerActions({reviewers, viewerLogin}: ReviewerActionsProps) {
  const viewerPending = findViewerPendingRequest(reviewers, viewerLogin)
  const viewerReview = findViewerCompletedReview(reviewers, viewerLogin)

  if (!viewerPending && !viewerReview) {
    return null
  }

  const items: ReactNode[] = []

  if (viewerPending) {
    items.push(<ReviewRequestedItem key="requested" request={viewerPending} />)
  }

  if (viewerReview) {
    const unresolvedCount = viewerReview.threadCounts.total - viewerReview.threadCounts.resolved
    if (unresolvedCount > 0) {
      items.push(<UnresolvedCommentsItem key="unresolved" review={viewerReview} unresolvedCount={unresolvedCount} />)
    }
  }

  if (viewerPending) {
    items.push(<WaitingOnOthersItem key="waiting" />)
  }

  if (items.length === 0) {
    return null
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ul role="list" className={styles.actionItemList}>
      {items}
    </ul>
  )
}

function ReviewRequestedItem({request}: {request: PendingReviewRequest}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)
  const isCodeOwner = request.isCodeOwner || request.isCodeOwnerFromDelegation
  const hasTeamDelegation = !!request.delegatedFromTeamName

  let message = undefined
  if (isCodeOwner && hasTeamDelegation) {
    message = `Assigned from ${request.delegatedFromTeamName}.`
  } else if (isCodeOwner) {
    message = 'Requested as a code owner.'
  } else if (hasTeamDelegation) {
    message = `Requested on behalf of ${request.delegatedFromTeamName}.`
  }

  const viewChangesPath = pullRequestsChangesRoute.generatePath({owner, repo, pr_number})

  return (
    <ActionItem
      leadingIcon={EyeIcon}
      action={{
        linkTo: viewChangesPath,
        label: 'View changes',
      }}
      title="Your review has been requested"
      description={message}
    />
  )
}

function UnresolvedCommentsItem({review, unresolvedCount}: {review: CompletedReview; unresolvedCount: number}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)
  const seeUnresolvedPath = pullRequestsChangesWithRangeRoute.generatePath(
    {owner, repo, pr_number, range: `BASE..${review.headSha}`},
    {search: {review_id: String(review.id), show_resolved: 'false'}},
  )

  const commentText =
    unresolvedCount === 1
      ? `${unresolvedCount} of your comments is unresolved.`
      : `${unresolvedCount} of your comments are unresolved.`

  return (
    <ActionItem
      leadingIcon={CommentDiscussionIcon}
      title={commentText}
      action={{linkTo: seeUnresolvedPath, label: 'View unresolved'}}
    />
  )
}

function WaitingOnOthersItem() {
  return <ActionItem leadingIcon={PeopleIcon} title="Others may still be waiting on you to take action." />
}

function findViewerPendingRequest(
  reviewers: ReviewRequestsData,
  viewerLogin: string,
): PendingReviewRequest | undefined {
  return reviewers.pendingReviewRequests?.find(
    req => req.reviewer.displayLogin.toLowerCase() === viewerLogin.toLowerCase(),
  )
}

function findViewerCompletedReview(reviewers: ReviewRequestsData, viewerLogin: string): CompletedReview | undefined {
  return reviewers.completedReviews?.find(rev => rev.reviewer.displayLogin.toLowerCase() === viewerLogin.toLowerCase())
}
